import { AnyAction } from "redux";
import { addDays, format } from "date-fns";

import {
  CLIENT_REGISTRATION_ENDPOINT,
  LOCATIONS_ENDPOINT,
  END_OF_TENANCY_AVAILABILITY_ENDPOINT,
  END_OF_TENANCY_PRICING_ENDPOINT,
  END_OF_TENANCY_RESERVATION_ENDPOINT,
  END_OF_TENANCY_WARRANTY_ENDPOINT,
  END_OF_TENANCY_CHECKOUT_ENDPOINT,
  END_OF_TENANCY_VOUCHER_ENDPOINT,
  END_OF_TENANCY_PAYMENT_METHODS_ENDPOINT,
} from "@config/endpoints";

import {
  ThunkActionDispatch,
  ThunkActionOptions,
} from "@typings/reduxThunkActions";
import { CALENDAR_WEEK_SHIFT } from "@typings/booking";
import { VoucherInfo } from "@typings/bookingsToValidate";
import { PAYMENT_METHODS } from "@typings/globals";

import { ReduxActionNameCreator } from "@services/ReduxActionNameCreator";
import { ASYNC_ACTION_TYPES } from "@services/FetchFacade";
import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { DATE_FORMAT } from "@services/DateFormatter";

import { asyncActionCreator } from "@app/services/AsyncActionCreator";
import { IStore } from "@app/redux/reducers";
import {
  EndOfTenancyRegistrationForm,
  LOCAL_STORAGE_KEY,
  Task,
} from "@app/redux/endOfTenancy/endOfTenancy.typings";
import {
  abortControllerHelper,
  setLocalStorageHelper,
  setFromLocalStorageHelper,
  deleteLocalStorageHelper,
} from "@app/redux/helpers";
import { genericPendingAction } from "@app/services/GenericPendingAction";

import {
  getReservationEndpointRequest,
  getAvailabilityEndpointRequest,
  getAddLocationEndpointRequest,
  getPricingEndpointRequest,
  getClientRegistrationEndpointRequest,
  getCheckoutEndpointRequest,
  getCustomAvailabilityEndpointRequest,
} from "./endOfTenancy.repository";

const action = new ReduxActionNameCreator("endOfTenancy");

const abortControllers = {
  fetchEndOfTenancyAvailability: abortControllerHelper(),
  fetchCustomEndOfTenancyAvailability: abortControllerHelper(),
  fetchWarrantyPriceAndCurrency: abortControllerHelper(),
  fetchPricings: abortControllerHelper(),
  fetchVoucher: abortControllerHelper(),
  fetchPaymentMethods: abortControllerHelper(),
};

export const FETCH_WARRANTY_PRICE_AND_CURRENCY = action.make(
  "fetch warranty price and currency",
);
export const FETCH_AVAILABILITY = action.make("fetch availability");
export const FETCH_CUSTOM_AVAILABILITY = action.make(
  "fetch custom availability",
);
export const FETCH_PRICING = action.make("fetch pricing");
export const FETCH_VOUCHER = action.make("fetch voucher");
export const RESET_VOUCHER = action.make("reset voucher");
export const FETCH_PAYMENT_METHODS = action.make("fetch payment methods");
export const SET_VOUCHER = action.make("set voucher");
export const SET_PAYMENT_METHOD = action.make("set payment method");
export const SET_TASK = action.make("set task");
export const SET_EXECUTION_DATE = action.make("set execution date");
export const SET_SPECIAL_INSTRUCTIONS = action.make("set special instructions");
export const SET_WARRANTY = action.make("set warranty");
export const SET_SQUARE_METERS = action.make("set square meters");
export const SET_SHOW_OFFER = action.make("set show offer");
export const SET_LOCATION_UUID = action.make("set location uuid");
export const SET_REGISTRATION_FORM = action.make("set registration form");
export const ADD_LOCATION = action.make("add location");
export const SET_SKIP_AGENT_ASSIGNMENT = action.make(
  "set skip agent assignment",
);
export const SET_REQUEST_DIFFERENT_STARTING_HOUR = action.make(
  "set request different starting hour",
);
export const SET_SERVICE_LOCATION = action.make("set service location");
export const MAKE_RESERVATION = action.make("make reservation");
export const REGISTER_CLIENT = action.make("register client");
export const MAKE_CHECKOUT = action.make("make checkout");
export const SET_IN_LOCAL_STORAGE = action.make("set in local storage");
export const SET_FROM_LOCAL_STORAGE = action.make("set from local storage");
export const DELETE_LOCAL_STORAGE = action.make("delete local storage");
export const CANCEL_RESERVATION = action.make("cancel reservation");
export const SET_ALL_TASKS_SELECTED = action.make("set all tasks selected");
export const SET_DEFAULT_SELECTED_TASKS = action.make(
  "set default selected tasks",
);
export const SET_QUERY_PARAMS = action.make("set query params");
export const LEAVE_PROCESS = action.make("leave process");
export const SET_WEEK_START_DATE_V2 = action.make("set week start date v2");

export const fetchEndOfTenancyAvailability =
  (weekShift?: CALENDAR_WEEK_SHIFT, opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(FETCH_AVAILABILITY));

    const body = getAvailabilityEndpointRequest(store(), weekShift);

    const action = await asyncActionCreator(
      {
        url: END_OF_TENANCY_AVAILABILITY_ENDPOINT,
        action: FETCH_AVAILABILITY,
        method: ASYNC_ACTION_TYPES.POST,
        body,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchEndOfTenancyAvailability(),
        }),
      },
    );

    return dispatch(action);
  };

export const fetchCustomEndOfTenancyAvailability =
  (chunkSize = 3, customWeekStartDate?: string, opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(FETCH_CUSTOM_AVAILABILITY));

    const body = getCustomAvailabilityEndpointRequest(
      store(),
      customWeekStartDate,
    );

    const action = await asyncActionCreator(
      {
        url: END_OF_TENANCY_AVAILABILITY_ENDPOINT,
        action: FETCH_CUSTOM_AVAILABILITY,
        method: ASYNC_ACTION_TYPES.POST,
        body,
        params: {
          chunkSize,
          isExtraRequest: !!customWeekStartDate,
        },
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchEndOfTenancyAvailability(),
        }),
      },
    );

    return dispatch(action);
  };

export const fetchWarrantyPriceAndCurrency =
  (opts?: ThunkActionOptions) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator(
      {
        url: END_OF_TENANCY_WARRANTY_ENDPOINT,
        action: FETCH_WARRANTY_PRICE_AND_CURRENCY,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchWarrantyPriceAndCurrency(),
        }),
      },
    );

    return dispatch(action);
  };

export const makeReservation =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getReservationEndpointRequest(store());

    const action = await asyncActionCreator({
      url: END_OF_TENANCY_RESERVATION_ENDPOINT,
      action: MAKE_RESERVATION,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const fetchPricings =
  (opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(FETCH_PRICING));

    const body = getPricingEndpointRequest(store());

    const action = await asyncActionCreator(
      {
        url: END_OF_TENANCY_PRICING_ENDPOINT,
        action: FETCH_PRICING,
        method: ASYNC_ACTION_TYPES.POST,
        body,
      },
      {
        ...(opts?.abortEnabled && { signal: abortControllers.fetchPricings() }),
      },
    );

    return dispatch(action);
  };

export const fetchVoucher =
  (token: string, opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const executionDate = store()
      .endOfTenancy.get("executionDate")
      .split("+")[0];
    const fallbackExecutionDate = addDays(new Date(), 2);
    const date = executionDate
      ? new Date(executionDate)
      : fallbackExecutionDate;

    const formattedDateWithTime = format(
      date,
      DATE_FORMAT.YEAR_MONTH_DAY_HOUR_MINUTE_SECOND_ISO_8601,
    );

    const action = await asyncActionCreator(
      {
        url: END_OF_TENANCY_VOUCHER_ENDPOINT.replace(
          "{tokenValue}",
          token,
        ).replace("{executionDateTime}", formattedDateWithTime),
        action: FETCH_VOUCHER,
        params: {
          token,
        },
      },
      {
        ...(opts?.abortEnabled && { signal: abortControllers.fetchVoucher() }),
      },
    );

    return dispatch(action);
  };

export const resetVoucher = (): AnyAction => ({
  type: RESET_VOUCHER,
});

export const fetchPaymentMethods =
  (opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator(
      {
        url: END_OF_TENANCY_PAYMENT_METHODS_ENDPOINT.replace(
          "{reservationUuid}",
          store().endOfTenancy.get("reservationUuid"),
        ).replace(
          "{displayedPriceValue}",
          store().endOfTenancy.getIn([
            "pricing",
            "displayedPriceForFirstBooking",
          ]),
        ),
        action: FETCH_PAYMENT_METHODS,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchPaymentMethods(),
        }),
      },
    );

    return dispatch(action);
  };

export const addLocation =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getAddLocationEndpointRequest(store());

    const action = await asyncActionCreator({
      url: LOCATIONS_ENDPOINT,
      action: ADD_LOCATION,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const registerClient =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getClientRegistrationEndpointRequest(store());

    const action = await asyncActionCreator({
      url: CLIENT_REGISTRATION_ENDPOINT,
      action: REGISTER_CLIENT,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const checkout =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getCheckoutEndpointRequest(store());

    const action = await asyncActionCreator({
      url: END_OF_TENANCY_CHECKOUT_ENDPOINT.replace(
        "{reservationUuid}",
        store().endOfTenancy.get("reservationUuid"),
      ),
      action: MAKE_CHECKOUT,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const setVoucher = (payload: VoucherInfo): AnyAction => ({
  type: SET_VOUCHER,
  payload,
});

export const setLocationUuid = (payload: string): AnyAction => ({
  type: SET_LOCATION_UUID,
  payload,
});

export const setPaymentMethod = (
  payload: PAYMENT_METHODS | null,
): AnyAction => ({
  type: SET_PAYMENT_METHOD,
  payload,
});

export const setExecutionDate = (payload: string): AnyAction => ({
  type: SET_EXECUTION_DATE,
  payload,
});

export const setTask = (payload: Task): AnyAction => ({
  type: SET_TASK,
  payload,
});

export const setWarranty = (warranty: boolean): AnyAction => ({
  type: SET_WARRANTY,
  payload: warranty,
});

export const setAllTasksSelected = (): AnyAction => ({
  type: SET_ALL_TASKS_SELECTED,
});

export const setDefaultSelectedTasks = (): AnyAction => ({
  type: SET_DEFAULT_SELECTED_TASKS,
});

export const setSquareMeters = (payload: number): AnyAction => ({
  type: SET_SQUARE_METERS,
  payload,
});

export const setShowOffer = (payload: boolean): AnyAction => ({
  type: SET_SHOW_OFFER,
  payload,
});

export const setSkipAgentAssignment = (
  skipAgentAssignment: boolean,
): AnyAction => ({
  type: SET_SKIP_AGENT_ASSIGNMENT,
  payload: skipAgentAssignment,
});

export const setRequestDifferentStartingHour = (
  requestDifferentStartingHour: boolean,
): AnyAction => ({
  type: SET_REQUEST_DIFFERENT_STARTING_HOUR,
  payload: requestDifferentStartingHour,
});

export const setServiceLocation = (payload: number): AnyAction => ({
  type: SET_SERVICE_LOCATION,
  payload,
});

export const setSpecialInstructions = (payload: string): AnyAction => ({
  type: SET_SPECIAL_INSTRUCTIONS,
  payload,
});

export const setRegistrationForm = (
  payload: Partial<EndOfTenancyRegistrationForm>,
): AnyAction => ({
  type: SET_REGISTRATION_FORM,
  payload,
});

export const cancelReservation = (): AnyAction => ({
  type: CANCEL_RESERVATION,
});

export const setQueryParams = (payload: string): AnyAction => ({
  type: SET_QUERY_PARAMS,
  payload,
});

export const leaveProcess = (): AnyAction => ({
  type: LEAVE_PROCESS,
});

export const setLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch, store: () => IStore): void => {
    setLocalStorageHelper(
      dispatch,
      SET_IN_LOCAL_STORAGE,
      store().endOfTenancy,
      LOCAL_STORAGE_KEY,
    );
  };

export const setFromLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch): void => {
    setFromLocalStorageHelper(
      dispatch,
      SET_FROM_LOCAL_STORAGE,
      LOCAL_STORAGE_KEY,
    );
  };

export const deleteLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch): void => {
    deleteLocalStorageHelper(dispatch, DELETE_LOCAL_STORAGE, LOCAL_STORAGE_KEY);
  };

export const setWeekStartDateV2 = (payload: string): AnyAction => ({
  type: SET_WEEK_START_DATE_V2,
  payload,
});
